import React, { useEffect, useState, useContext, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Pagination, Popconfirm, message } from "antd";
import { StarOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { ThemeContext } from "../../context/ThemeContext";
import LandingLayout from "./LandingLayout";
import apiClient from "../../services/apiClient";
import { usePortfolioUser } from "./Portfolio/usePortfolioUser";
import "./Watchlist.css";
import "./Instruments.css";
import "./Portfolio/PortfolioHeader.css";
import "./Portfolio/HighReturnPage.css";
import "./Portfolio/PortfolioRefineFilters.css";
import "./Portfolio/PortfolioTables.css";

const WATCHLIST_PAGE_SIZE = 12;

const formatCoupon = (value) => {
  if (value == null || value === "") return "—";
  const s = String(value).replace(/%/g, "").trim();
  return s ? `${s}%` : "—";
};

const getShortAgencyName = (fullName) => {
  if (!fullName) return fullName;
  const upperName = fullName.toUpperCase();
  if (upperName.includes("CRISIL")) return "CRISIL";
  if (upperName.includes("ICRA")) return "ICRA";
  if (upperName.includes("CARE") || upperName.includes("CREDIT ANALYSIS")) return "CARE";
  if (upperName.includes("BRICKWORK")) return "BRICKWORK";
  if (upperName.includes("INDIA RATING") || upperName.includes("INDIA RATINGS")) return "INDIA";
  if (upperName.includes("ACUITE")) return "ACUITE";
  if (upperName.includes("INFOMERICS")) return "INFOMERICS";
  return fullName;
};

const formatWatchlistRating = (creditRatings) => {
  if (!Array.isArray(creditRatings) || creditRatings.length === 0) return "—";
  const primary =
    creditRatings.find((r) =>
      String(r?.currentRating || "")
        .toUpperCase()
        .includes("AAA")
    ) || creditRatings[0];
  if (!primary?.currentRating) return "—";
  const rating = String(primary.currentRating)
    .replace(/PP\s*-\s*MLD/gi, "")
    .replace(/PPMLD/gi, "")
    .replace(/\(\s*CE\s*\)/gi, "")
    .trim();
  const agency = getShortAgencyName(primary.creditRatingAgencyName);
  return agency ? `${rating} by ${agency}` : rating;
};

const getIssuerName = (item) =>
  item?.companyIssuerName || item?.issuerName || item?.issuer || "Unknown issuer";

const getRedemption = (item) =>
  item?.redemption || item?.dateOfRedemption || item?.maturityDate || "—";

const WatchlistCard = ({
  item,
  updating,
  onViewDetails,
  onRemove,
}) => {
  const isin = item?.isin || "—";
  const issuer = getIssuerName(item);
  const rating = formatWatchlistRating(item?.creditRatings);
  const industry = item?.industry || item?.basicIndustry || item?.sector;

  return (
    <article className="watchlist-card">
      <div className="watchlist-card__content">
        <div className="watchlist-card__identity">
          <div className="watchlist-card__badges">
            <span className="watchlist-card__badge watchlist-card__badge--tracked">
              Tracked
            </span>
            <span
              className={`watchlist-card__badge watchlist-card__badge--rating${
                rating === "—" ? " watchlist-card__badge--muted" : ""
              }`}
              title={rating}
            >
              {rating}
            </span>
          </div>
          <h3 className="watchlist-card__issuer" title={issuer}>
            {issuer}
          </h3>
          <div className="watchlist-card__meta">
            <span className="watchlist-card__isin-label">ISIN</span>
            <code className="watchlist-card__isin-code">{isin}</code>
            {industry && <span className="watchlist-card__industry">{industry}</span>}
          </div>
        </div>

        <div className="watchlist-card__metrics">
          <div className="watchlist-card__metric watchlist-card__metric--highlight">
            <span className="watchlist-card__metric-label">Coupon</span>
            <span className="watchlist-card__metric-value">{formatCoupon(item?.couponRate)}</span>
          </div>
          <div className="watchlist-card__metric">
            <span className="watchlist-card__metric-label">Redemption</span>
            <span className="watchlist-card__metric-value">{getRedemption(item)}</span>
          </div>
        </div>
      </div>

      <div className="watchlist-card__actions">
        <Button
          size="small"
          icon={<EyeOutlined />}
          className="watchlist-card__btn watchlist-card__btn--view"
          onClick={() => onViewDetails(item?.isin)}
        >
          View details
        </Button>
        <Popconfirm
          title="Remove from watchlist?"
          description={`${issuer} (${isin}) will be removed from your watchlist.`}
          okText="Remove"
          cancelText="Cancel"
          okButtonProps={{ danger: true }}
          disabled={updating}
          onConfirm={() => onRemove(item?.isin)}
        >
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            loading={updating}
            className="watchlist-card__btn watchlist-card__btn--remove"
          >
            Remove
          </Button>
        </Popconfirm>
      </div>
    </article>
  );
};

const WatchlistPanel = ({ embedded = false }) => {
  const { isDark } = useContext(ThemeContext);
  const navigate = useNavigate();
  const { userId, authHeaders, requireAuth } = usePortfolioUser();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingIsin, setUpdatingIsin] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(WATCHLIST_PAGE_SIZE);

  const totalItems = items.length;
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, currentPage, pageSize]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [items.length, pageSize, currentPage]);

  const baseUrl = process.env.REACT_APP_BASE_URL || "";

  const fetchWatchlist = useCallback(async () => {
    const auth = requireAuth("Please log in to view your watchlist");
    if (!auth.ok) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await apiClient.get(`${baseUrl}/user/watchlist`, {
        headers: authHeaders(),
      });
      setItems(response.data?.data || []);
    } catch (err) {
      console.error("Error fetching watchlist:", err);
      message.error("Failed to load watchlist");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, baseUrl, requireAuth]);

  useEffect(() => {
    if (userId) {
      fetchWatchlist();
    } else {
      setLoading(false);
    }
  }, [userId, fetchWatchlist]);

  const handleViewDetails = useCallback(
    (isin) => {
      if (!isin) return;
      navigate(`/isin-details/${isin}`);
    },
    [navigate]
  );

  const handleRemove = useCallback(
    async (isin) => {
      const auth = requireAuth("Please log in to manage your watchlist");
      if (!auth.ok) return;
      try {
        setUpdatingIsin(isin);
        await apiClient.delete(`${baseUrl}/user/watchlist/${isin}`, {
          headers: authHeaders(),
        });
        message.success("Removed from watchlist");
        setItems((prev) => prev.filter((i) => i.isin !== isin));
      } catch (err) {
        console.error("Error removing from watchlist:", err);
        message.error("Failed to remove from watchlist");
      } finally {
        setUpdatingIsin(null);
      }
    },
    [authHeaders, baseUrl, requireAuth]
  );

  return (
    <div className={`watchlist-page hr-page isin-page${isDark ? " dark" : ""}${embedded ? " watchlist-page--embedded" : ""}`}>
      <section className="hr-bonds-section watchlist-section">
        {!embedded && (
          <header className="instruments-page-header">
            <h1 className="instruments-page-title">
              <StarOutlined className="instruments-page-icon" />
              My Watchlist
            </h1>
            <p className="instruments-page-hint">
              Bonds and ISINs you are tracking.
            </p>
          </header>
        )}

        <div className="hr-results-bar watchlist-results-bar">
          <div className="hr-results-bar__left">
            <span className="hr-results-bar__label">Bond results</span>
            <span className="hr-results-count">
              {loading ? (
                "Loading…"
              ) : totalItems > 0 ? (
                <>
                  <strong>{totalItems.toLocaleString()}</strong>
                  {` ${totalItems === 1 ? "bond" : "bonds"}`}
                  {totalItems > pageSize && (
                    <span className="hr-results-count__page">
                      {` · showing ${(currentPage - 1) * pageSize + 1}–${Math.min(
                        currentPage * pageSize,
                        totalItems
                      )}`}
                    </span>
                  )}
                </>
              ) : (
                <>
                  <strong>0</strong> bonds
                </>
              )}
            </span>
          </div>
        </div>

        <div className="watchlist-content">
          {loading ? (
            <div className="hr-loading">
              <div className="hr-spinner" />
              <p>Loading watchlist…</p>
            </div>
          ) : items.length === 0 ? (
            <div className="hr-empty watchlist-empty">
              <span className="hr-empty-icon">★</span>
              <h3>No ISINs in your watchlist yet</h3>
              <p>
                Browse instruments and use <strong>Add to Watchlist</strong> on
                any detail page.
              </p>
              {!embedded && (
                <button
                  type="button"
                  className="hr-btn-apply"
                  onClick={() => navigate("/isins")}
                >
                  Browse Instruments
                </button>
              )}
            </div>
          ) : (
            <div className="watchlist-card-list" role="list" aria-label="Watchlist bonds">
              {paginatedItems.map((item, index) => (
                <WatchlistCard
                  key={item.isin || index}
                  item={item}
                  updating={updatingIsin === item.isin}
                  onViewDetails={handleViewDetails}
                  onRemove={handleRemove}
                />
              ))}
            </div>
          )}

          {totalItems > 0 && !loading && (
            <div className="hr-pagination watchlist-pagination">
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={totalItems}
                onChange={(page, size) => {
                  setCurrentPage(page);
                  setPageSize(size);
                }}
                onShowSizeChange={(page, size) => {
                  setCurrentPage(page);
                  setPageSize(size);
                }}
                showSizeChanger
                pageSizeOptions={["12", "24", "48", "96"]}
                className="hr-ant-pagination"
              />
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

const Watchlist = () => (
  <LandingLayout>
    <WatchlistPanel />
  </LandingLayout>
);

export { WatchlistPanel };
export default Watchlist;
